/**
 * TypeScript types for Expense Invoices feature
 * Based on backend DTOs from TravelCRM.Application.ExpenseInvoices
 */

/**
 * Expense Invoice Status Enum
 * Lifecycle: Draft → Received → Verified → Paid
 */
export enum ExpenseInvoiceStatus {
  Draft = 'draft',
  Received = 'received',
  Verified = 'verified',
  Paid = 'paid',
  Cancelled = 'cancelled',
}

export enum ExpenseInvoiceDocumentKind {
  FullInvoice = 'fullInvoice',
  SimplifiedCost = 'simplifiedCost',
}

export const expenseInvoiceFa3Families = [
  'prepayment',
  'finalSettlement',
  'standardCorrection',
  'prepaymentCorrection',
  'settlementCorrection',
  'formalCorrection',
  'collectiveCorrection',
  'mixedTaxation',
  'margin',
  'simplified',
  'intraCommunitySupply',
  'export',
  'standardVat',
] as const;

export type ExpenseInvoiceFa3Family = (typeof expenseInvoiceFa3Families)[number];

export const expenseInvoiceFa3Dispositions = ['autoImport', 'reviewRequired'] as const;

export type ExpenseInvoiceFa3Disposition = (typeof expenseInvoiceFa3Dispositions)[number];

export const expenseInvoiceFa3SupportStatuses = ['supported', 'unsupported'] as const;

export type ExpenseInvoiceFa3SupportStatus = (typeof expenseInvoiceFa3SupportStatuses)[number];

export const expenseInvoiceFa3CorrectionVariants = [
  'beforeAfter',
  'difference',
  'formalOnly',
  'collectivePeriod',
] as const;

export type ExpenseInvoiceFa3CorrectionVariant =
  (typeof expenseInvoiceFa3CorrectionVariants)[number];

export const expenseInvoiceFa3LineSources = ['faWiersz', 'zamowienieWiersz', 'none'] as const;

export type ExpenseInvoiceFa3LineSource = (typeof expenseInvoiceFa3LineSources)[number];

export const expenseInvoiceFa3LineRoles = [
  'before',
  'after',
  'order',
  'current',
  'delta',
  'referenceOnly',
] as const;

export type ExpenseInvoiceFa3LineRole = (typeof expenseInvoiceFa3LineRoles)[number];

export const expenseInvoiceFa3RelationTypes = [
  'correctedDocument',
  'prepaymentDocument',
  'settlementDocument',
] as const;

export type ExpenseInvoiceFa3RelationType = (typeof expenseInvoiceFa3RelationTypes)[number];

export enum ExpenseInvoiceDraftBlockReason {
  None = 'None',
  SupplierApprovalPending = 'SupplierApprovalPending',
  ImportDataReviewRequired = 'ImportDataReviewRequired',
}

export type ExpenseInvoiceDraftBlockReasonDto = ExpenseInvoiceDraftBlockReason | string | null;

export interface ExpenseInvoiceTagDto {
  id: number;
  name: string;
}

export interface ExpenseInvoiceFa3SummaryDto {
  family: ExpenseInvoiceFa3Family;
  disposition: ExpenseInvoiceFa3Disposition;
  supportStatus: ExpenseInvoiceFa3SupportStatus;
  correctionVariant: ExpenseInvoiceFa3CorrectionVariant | null;
  unsupportedReason: string | null;
  hasUnresolvedLinks: boolean;
}

export interface ExpenseInvoiceFa3DocumentLinkDto {
  relationType: ExpenseInvoiceFa3RelationType;
  relatedExpenseInvoiceId: number | null;
  relatedDocumentReference: string | null;
  externalInvoiceNumber: string | null;
  externalKsefNumber: string | null;
  externalIssueDate: string | null;
  isResolved: boolean;
}

export interface ExpenseInvoiceFa3SemanticDto {
  family: ExpenseInvoiceFa3Family;
  supportStatus: ExpenseInvoiceFa3SupportStatus;
  sourceDocumentKind: string;
  disposition: ExpenseInvoiceFa3Disposition;
  lineSource: ExpenseInvoiceFa3LineSource;
  correctionVariant: ExpenseInvoiceFa3CorrectionVariant | null;
  correctionReason: string | null;
  sourceKsefNumber: string | null;
  unsupportedReason: string | null;
  links: ExpenseInvoiceFa3DocumentLinkDto[];
}

export interface ExpenseInvoiceItemFa3SemanticDto {
  lineRole: ExpenseInvoiceFa3LineRole;
  sourceSection: string;
  sourceLineNumber: number | null;
}

/**
 * List item DTO (for paginated list)
 */
export interface ExpenseInvoiceListDto {
  id: number;
  documentKind: ExpenseInvoiceDocumentKind | 'FullInvoice' | 'SimplifiedCost';
  documentReference: string;
  displayIssuerName: string;
  invoiceNumber: string | null;
  issueDate: string; // ISO date
  supplierName: string | null;
  supplierNip: string | null;
  companyId: number | null;
  companyName: string | null;
  description: string | null;
  tags: ExpenseInvoiceTagDto[];
  totalGross: number;
  currency: string;
  status: ExpenseInvoiceStatus;
  draftBlockReason?: ExpenseInvoiceDraftBlockReasonDto;
  isPaid: boolean;
  importJobId: number | null;
  createdAt: string; // ISO datetime
  fa3Summary?: ExpenseInvoiceFa3SummaryDto | null;
}

/**
 * Full details DTO
 */
export interface ExpenseInvoiceDto {
  id: number;
  documentKind: ExpenseInvoiceDocumentKind | 'FullInvoice' | 'SimplifiedCost';
  documentReference: string;
  displayIssuerName: string;
  invoiceNumber: string | null;
  issueDate: string; // ISO date
  saleDate: string | null; // ISO date
  paymentDueDate: string | null; // ISO date

  // Supplier info
  supplierName: string | null;
  supplierAddress: string | null;
  supplierNip: string | null;
  supplierBankAccount: string | null;

  // Company (buyer)
  companyId: number | null;
  companyName: string | null;

  // Amounts
  totalNet: number;
  totalVat: number;
  totalGross: number;
  currency: string;

  // Payment
  status: ExpenseInvoiceStatus;
  draftBlockReason?: ExpenseInvoiceDraftBlockReasonDto;
  isPaid: boolean;
  paidDate: string | null; // ISO date
  paidAmount: number | null;
  paymentNote: string | null;

  // Items
  items: ExpenseInvoiceItemDto[];

  // Metadata
  importJobId: number | null;
  description: string | null;
  tags: ExpenseInvoiceTagDto[];
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  fa3Semantic?: ExpenseInvoiceFa3SemanticDto | null;

  /**
   * List of bookings assigned to this expense invoice with allocated amounts.
   */
  bookings?: BookingSummaryDto[];
}

/**
 * Invoice item DTO
 */
export interface ExpenseInvoiceItemDto {
  id: number;
  name: string;
  quantity: number;
  unitNet: number;
  unitGross: number;
  vatRate: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  isVatExempt: boolean;
  vatExemptNote: string | null;
  fa3Semantic?: ExpenseInvoiceItemFa3SemanticDto | null;
}

/**
 * Create DTO
 */
export interface CreateExpenseInvoiceDto {
  invoiceNumber: string;
  issueDate: string; // ISO date
  saleDate?: string | null;
  paymentDueDate?: string | null;

  supplierName: string;
  supplierAddress?: string | null;
  supplierNip?: string | null;
  supplierBankAccount?: string | null;

  companyId?: number | null;
  currency: string;
  status?: ExpenseInvoiceStatus; // Optional: defaults to Draft if not specified
  notes?: string | null;

  items: CreateExpenseInvoiceItemDto[];
}

/**
 * Create item DTO
 */
export interface CreateExpenseInvoiceItemDto {
  name: string;
  quantity: number;
  unitNet: number;
  unitGross: number;
  vatRate: number;
  isVatExempt: boolean;
  vatExemptNote?: string | null;
}

/**
 * Update DTO (same as Create)
 */
export type UpdateExpenseInvoiceDto = CreateExpenseInvoiceDto;
export type UpdateExpenseInvoiceItemDto = CreateExpenseInvoiceItemDto;

export interface CreateSimplifiedExpenseInvoiceDto {
  companyId: number;
  issueDate: string;
  totalGross: number;
  currency: string;
  description: string;
  tagIds: number[];
}

export type UpdateSimplifiedExpenseInvoiceDto = CreateSimplifiedExpenseInvoiceDto;

/**
 * Paginated response
 */
export interface ExpenseInvoicePagedListDto {
  items: ExpenseInvoiceListDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Query params for list
 */
export interface ExpenseInvoiceQueryParams {
  pageNumber?: number;
  pageSize?: number;
  companyId?: number;
  status?: ExpenseInvoiceStatus;
  isPaid?: boolean;
  supplierName?: string;
  issueDateFrom?: string; // ISO date
  issueDateTo?: string; // ISO date
  searchTerm?: string;
  // Verified against GetExpenseInvoicesQuery.cs (S2 backend)
  invoiceNumber?: string;
  supplierCity?: string;
  documentKind?: 'FullInvoice' | 'SimplifiedCost';
  isAssignedToAnyBooking?: boolean;
}

/**
 * DTO for booking assignment with required allocated amount.
 * Used when assigning bookings to expense invoices.
 */
export interface BookingAssignmentDto {
  /**
   * Booking identifier to assign.
   */
  bookingId: number;

  /**
   * Portion of the expense invoice gross amount allocated to this booking.
   * Must be greater than 0.
   * Sum across all assignments cannot exceed invoice TotalGross.
   */
  allocatedAmount: number;

  /**
   * Optional expense type tag assigned specifically for this booking assignment.
   * - omitted -> preserve existing assignment tag
   * - null -> clear assignment tag
   */
  tagId?: number | null;
}

/**
 * DTO for displaying assigned bookings in expense invoice details.
 */
export interface BookingSummaryDto {
  id: number;
  offerId: number;
  offerName: string;
  bookingDateFrom: string; // ISO date (yyyy-MM-dd)
  bookingDateTo: string; // ISO date (yyyy-MM-dd)
  allocatedAmount: number; // Amount allocated to this booking
  assignmentTagId?: number | null;
  assignmentTagName?: string | null;
}

/**
 * Confidence tier for a booking suggestion, derived from `score` by the backend.
 */
export const bookingSuggestionConfidenceLevels = ['high', 'medium', 'low'] as const;

export type BookingSuggestionConfidence = (typeof bookingSuggestionConfidenceLevels)[number];

/**
 * DTO for an explainable, read-only booking suggestion for an expense invoice.
 * Selecting a suggestion never persists an assignment by itself.
 */
export interface BookingSuggestionDto {
  bookingId: number;
  offerName: string;
  bookingDateFrom: string; // ISO date (yyyy-MM-dd)
  bookingDateTo: string; // ISO date (yyyy-MM-dd)
  numberOfParticipants: number;
  score: number;
  confidence: BookingSuggestionConfidence;
  reasons: string[];
}

/**
 * Settlement preview for supplier prepayment allocation to expense invoice.
 */
export interface ExpenseInvoiceSettlementPreviewDto {
  expenseInvoiceId: number;
  companyId: number;
  currency: string;
  totalGross: number;
  paidAmount: number;
  dueAmount: number;
  openPrepaymentBalance: number;
  settledFromPrepayment: number;
}

/**
 * Request payload for settling expense invoice from supplier prepayment balance.
 */
export interface SettleExpenseInvoiceFromPrepaymentRequest {
  invoiceId: number;
  amount: number;
  createdByUserId: number;
  description?: string;
  bankDocumentEntryId?: number;
  bookingId?: number;
}

/**
 * Result returned after supplier prepayment mutation.
 */
export interface SupplierPrepaymentMutationResultDto {
  ledgerId: number;
  movementId: number;
  settlementId: number | null;
  companyId: number;
  currency: string;
  amount: number;
  openBalanceAfter: number;
  expenseInvoiceId: number | null;
  expenseInvoiceDueAfter: number | null;
  executedAtUtc: string;
  executedByUserId: number;
  sourceBankDocumentEntryId: number | null;
}

/**
 * Settlement row linked to selected expense invoice.
 */
export interface ExpenseInvoiceSettlementListItemDto {
  settlementId: number;
  supplierPrepaymentMovementId: number;
  settledAmount: number;
  createdAtUtc: string;
}

/**
 * Request payload for releasing previously created supplier prepayment allocation.
 */
export interface ReleaseSupplierPrepaymentAllocationRequest {
  settlementId: number;
  createdByUserId: number;
  description?: string;
}

/**
 * Result of finalizing an OCR-imported expense invoice draft pending operator review,
 * transitioning it from Draft to Received (R9).
 */
export interface FinalizeExpenseInvoiceImportReviewResultDto {
  expenseInvoiceId: number;
  status: ExpenseInvoiceStatus;
}
